import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FolderKanban,
  Users,
  CheckCircle,
  Clock,
  Plus,
  ArrowRight,
  TrendingUp,
  Calendar,
  Loader2
} from 'lucide-react';
import { useAuth } from './AppUnderTest';
import apiService from './apiService';

const Dashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);

      try {
        // Fetch all dashboard data in parallel (multiple network calls)
        const [statsRes, activityRes, deadlinesRes, metricsRes] = await Promise.all([
          apiService.getDashboardStats(),
          apiService.getRecentActivity(),
          apiService.getUpcomingDeadlines(),
          apiService.getPerformanceMetrics()
        ]);

        // Transform stats into display format
        const statsData = [
          { id: 'projects', label: 'Total Projects', value: statsRes.stats.totalProjects, icon: FolderKanban, link: '/projects' },
          { id: 'team', label: 'Team Members', value: statsRes.stats.teamMembers, icon: Users, link: '/projects' },
          { id: 'completed', label: 'Completed Tasks', value: statsRes.stats.completedTasks, icon: CheckCircle, link: '/projects' },
          { id: 'pending', label: 'Pending Tasks', value: statsRes.stats.pendingTasks, icon: Clock, link: '/projects' },
        ];

        setStats(statsData);
        setRecentActivity(activityRes.activities);
        setUpcomingDeadlines(deadlinesRes.deadlines);
        setMetrics(metricsRes.metrics);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="aut-dashboard" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 size={48} className="aut-spinner" style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: '16px', color: 'var(--aut-text-muted)' }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="aut-dashboard">
      <div className="aut-page-header">
        <h1 className="aut-page-title">Welcome back, {user?.name?.split(' ')[0] || 'User'}</h1>
        <p className="aut-page-description">Here's what's happening with your projects today.</p>
      </div>

      {/* Stats Grid */}
      <div className="aut-dashboard-grid">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.id} to={stat.link} className="aut-stat-card">
              <div className="aut-stat-icon">
                <Icon size={24} />
              </div>
              <h3 className="aut-stat-value">{stat.value}</h3>
              <p className="aut-stat-label">{stat.label}</p>
            </Link>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="aut-section">
        <div className="aut-section-header">
          <h2 className="aut-section-title">Quick Actions</h2>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Link to="/create-project" className="aut-btn aut-btn-primary">
            <Plus size={18} />
            <span>New Project</span>
          </Link>
          <Link to="/projects" className="aut-btn aut-btn-secondary">
            <FolderKanban size={18} />
            <span>View Projects</span>
          </Link>
          <Link to="/profile" className="aut-btn aut-btn-secondary">
            <Users size={18} />
            <span>Edit Profile</span>
          </Link>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
        {/* Recent Activity */}
        <div className="aut-section">
          <div className="aut-section-header">
            <h2 className="aut-section-title">Recent Activity</h2>
            <button className="aut-btn aut-btn-ghost aut-btn-sm">
              View all
              <ArrowRight size={16} />
            </button>
          </div>
          <div className="aut-card">
            <div className="aut-table-container">
              <table className="aut-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Item</th>
                    <th>User</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {recentActivity.map((activity) => (
                    <tr key={activity.id}>
                      <td>{activity.action}</td>
                      <td style={{ fontWeight: 500 }}>{activity.item}</td>
                      <td>{activity.user}</td>
                      <td style={{ color: 'var(--aut-text-muted)' }}>{activity.time}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Upcoming Deadlines */}
        <div className="aut-section" data-testid="upcoming-deadlines-section">
          <div className="aut-section-header">
            <h2 className="aut-section-title">Upcoming Deadlines</h2>
          </div>
          <div className="aut-card" data-testid="upcoming-deadlines-list">
            {upcomingDeadlines.map((deadline, index) => (
              <div
                key={deadline.id}
                data-testid={`deadline-item-${deadline.id}`}
                style={{
                  padding: '16px 0',
                  borderBottom: index < upcomingDeadlines.length - 1 ? '1px solid var(--aut-border-light)' : 'none'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <Calendar size={16} style={{ color: 'var(--aut-accent)' }} />
                  <span style={{ fontWeight: 500 }} data-testid="deadline-project-name">{deadline.project}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '28px' }}>
                  <span style={{ color: 'var(--aut-text-secondary)', fontSize: '14px' }}>{deadline.task}</span>
                  <span style={{ color: 'var(--aut-text-muted)', fontSize: '14px' }}>{deadline.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance Overview */}
      <div className="aut-section">
        <div className="aut-section-header">
          <h2 className="aut-section-title">Performance Overview</h2>
        </div>
        <div className="aut-card">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '40px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <TrendingUp size={18} style={{ color: 'var(--aut-success)' }} />
                <span style={{ color: 'var(--aut-text-secondary)', fontSize: '14px' }}>Task Completion Rate</span>
              </div>
              <div style={{ fontSize: '32px', fontWeight: 600, marginBottom: '8px' }}>{metrics?.taskCompletionRate || 0}%</div>
              <div className="aut-progress">
                <div className="aut-progress-bar" style={{ width: `${metrics?.taskCompletionRate || 0}%` }}></div>
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <TrendingUp size={18} style={{ color: 'var(--aut-success)' }} />
                <span style={{ color: 'var(--aut-text-secondary)', fontSize: '14px' }}>On-time Delivery</span>
              </div>
              <div style={{ fontSize: '32px', fontWeight: 600, marginBottom: '8px' }}>{metrics?.onTimeDelivery || 0}%</div>
              <div className="aut-progress">
                <div className="aut-progress-bar" style={{ width: `${metrics?.onTimeDelivery || 0}%` }}></div>
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <TrendingUp size={18} style={{ color: 'var(--aut-success)' }} />
                <span style={{ color: 'var(--aut-text-secondary)', fontSize: '14px' }}>Team Utilization</span>
              </div>
              <div style={{ fontSize: '32px', fontWeight: 600, marginBottom: '8px' }}>{metrics?.teamUtilization || 0}%</div>
              <div className="aut-progress">
                <div className="aut-progress-bar" style={{ width: `${metrics?.teamUtilization || 0}%` }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
